<template>
  <div class="signature_content_padding_top_small">
    <div class="signature_field">
      <!-- Loading state for lazy-loaded signatures -->
      <div v-if="isLoading" class="signature-loading">
        <i class="el-icon-loading"></i>
        <span>Loading...</span>
      </div>
      <!-- Actual signature image -->
      <img v-else-if="displaySignature" :src="displaySignature" height="50" loading="lazy">
      <p v-else>N/A</p>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';

// Local decode cache for base64 decoding (avoid repeat atob calls)
const decodeCache = new Map();
const MAX_DECODE_CACHE = 100;

// Cache for fetched signatures to avoid duplicate API calls
const signatureCache = new Map();
const MAX_SIGNATURE_CACHE = 50;

function decodeSignature(data) {
  if (!data || data === 'null' || data === 'N/A') return null;
  
  // Check decode cache first
  if (decodeCache.has(data)) {
    return decodeCache.get(data);
  }
  
  let result;
  try {
    // Signature is double base64 encoded - decode once to get data:image/png;base64,...
    result = window.atob(data);
  } catch (err) {
    // If decode fails, use as-is (might already be decoded)
    result = data;
  }
  
  // Cache the decoded result
  if (decodeCache.size >= MAX_DECODE_CACHE) {
    const firstKey = decodeCache.keys().next().value;
    decodeCache.delete(firstKey);
  }
  decodeCache.set(data, result);
  
  return result;
}

export default {
  props: {
    signatureData: {
      type: String,
      default: null
    },
    auditLogId: {
      type: Number,
      default: null
    },
    valueType: {
      type: String,
      default: 'new' // 'old' or 'new' - which value to display from the fetched signature
    }
  },
  data() {
    return {
      isLoading: false,
      fetchedSignature: null
    };
  },
  computed: {
    displaySignature() {
      // If we fetched the signature, use that
      if (this.fetchedSignature) {
        return decodeSignature(this.fetchedSignature);
      }
      // If it's a placeholder, don't show anything (will trigger fetch)
      if (this.signatureData === '[SIGNATURE_PLACEHOLDER]') {
        return null;
      }
      // Otherwise decode and display the provided data
      return decodeSignature(this.signatureData);
    },
    needsLazyLoad() {
      return this.signatureData === '[SIGNATURE_PLACEHOLDER]' && this.auditLogId;
    }
  },
  watch: {
    signatureData: {
      immediate: true,
      handler(newVal) {
        if (newVal === '[SIGNATURE_PLACEHOLDER]' && this.auditLogId) {
          this.fetchSignature();
        }
      }
    }
  },
  methods: {
    async fetchSignature() {
      if (!this.auditLogId) return;
      
      const cacheKey = `${this.auditLogId}-${this.valueType}`;
      
      // Check cache first
      if (signatureCache.has(cacheKey)) {
        this.fetchedSignature = signatureCache.get(cacheKey);
        return;
      }
      
      this.isLoading = true;
      try {
        const response = await axios.get(`/api/orderauditlogs/signature/${this.auditLogId}`);
        const data = response.data;
        
        // Store in cache
        if (signatureCache.size >= MAX_SIGNATURE_CACHE) {
          const firstKey = signatureCache.keys().next().value;
          signatureCache.delete(firstKey);
        }
        
        // Cache both old and new values
        signatureCache.set(`${this.auditLogId}-old`, data.oldValues);
        signatureCache.set(`${this.auditLogId}-new`, data.newValues);
        
        // Set the appropriate value
        this.fetchedSignature = this.valueType === 'old' ? data.oldValues : data.newValues;
      } catch (error) {
        console.error('Failed to load signature:', error);
        this.fetchedSignature = null;
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>


<style scoped>
@import "../../../styles/custom_alert.css";

.signature {
  background-color: #ffffff;
  border: 3px solid #000000;
}

.signature-loading {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #409EFF;
  font-size: 12px;
}
</style>
